import { ctxIsTemplate } from '@ContractBuilder/rules/block/is-ctx';
import { type EntityData, isEndorsementData } from '@ContractBuilder/types';
import type { DocumentContext } from '@ContractBuilder/utils/get-document-context';
import { getBlockCommentsCount } from '@features/comments/utils';
import { isReviewStatus } from '@helpers/status';
import type { ResourceBlock } from '@root/@types/base';
import type { BaseUser } from '@root/@types/types';
import { isClauseBlock } from '@root/helpers/blocks';
import { isSuperadmin } from '@root/helpers/permissions';

export interface DeriveContractBlockActionsInputs {
  blockId: string;
  blockType: ResourceBlock['type'];
  canEditOnTemplate: boolean;
  context: DocumentContext;
  user: BaseUser | null;
  submission: EntityData | undefined;
  isDeletable: boolean;
  isEditable: boolean;
  commentsFeatureEnabled: boolean;
}

export interface ContractBlockActionsDisplayState {
  isClause: boolean;
  isTemplate: boolean;
  isReviewStatus: boolean;
  commentCount: number;
  unresolvedCommentCount: number;
  showCommentsActions: boolean;
  canViewInLibrary: boolean;
  isEditable: boolean;
  isDeletable: boolean;
}

export const deriveContractBlockActionsState = ({
  blockId,
  blockType,
  canEditOnTemplate,
  context,
  user,
  submission,
  isDeletable,
  isEditable,
  commentsFeatureEnabled,
}: DeriveContractBlockActionsInputs): ContractBlockActionsDisplayState => {
  const isClause = isClauseBlock({ type: blockType });
  const isTemplate = ctxIsTemplate(context);
  const isReview = isReviewStatus(submission?.status);
  const { total: commentCount, unresolved: unresolvedCommentCount } = getBlockCommentsCount(blockId, submission);
  const showCommentsActions =
    commentsFeatureEnabled && !isEndorsementData(submission) && (isReview || commentCount > 0);
  const canViewInLibrary = isTemplate && (canEditOnTemplate || isSuperadmin(user ?? null));

  return {
    isClause,
    isTemplate,
    isReviewStatus: isReview,
    commentCount,
    unresolvedCommentCount,
    showCommentsActions,
    canViewInLibrary,
    isEditable,
    isDeletable,
  };
};
